import { $fetch, Headers, FetchOptions, FetchError } from 'ofetch';
import { showError, useRoute, useRuntimeConfig, clearError } from '#imports';
import { useAuthUserStore } from '~/store/auth-user';
import { H3Error } from 'h3';
import { useOrganizationStore } from '~/store/organization';
import { usePreviewStore } from '~/store/preview';
import { navigateTo } from '#app';
import { consola } from 'consola/basic';
import { useFirebase } from '~/composables/use-firebase';
import { resolveApiBaseUrl } from '~/api/base-url';
import {
  organizationDomainHeaderName,
  organizationSlugHeaderName,
} from '~/api/organization-headers';

/**
 * Resolves the Bearer credential for an API request.
 *
 * - Browser: a fresh Firebase ID token (the SDK auto-refreshes it, so there is
 *   no 14-day session-cookie cap). Waits for the auth state to be restored so
 *   early requests don't miss the token.
 * - Server (SSR): the ID token the auth service worker injected into the
 *   incoming navigation request's Authorization header.
 */
const resolveAuthToken = async (
  incomingAuthorization: string | undefined,
): Promise<string | null> => {
  if (import.meta.client) {
    const { auth } = useFirebase();
    await auth.authStateReady();
    const { currentUser } = auth;
    return currentUser ? currentUser.getIdToken() : null;
  }

  if (incomingAuthorization) {
    return incomingAuthorization.replace(/^Bearer\s+/i, '');
  }

  return null;
};

export {
  organizationDomainHeaderName,
  organizationSlugHeaderName,
} from '~/api/organization-headers';
export const ORGANIZATION_UNAVAILABLE_STATUS = 410;
export const previewRoleHeaderName = 'x-preview-role-id';

const setDefaultHeader = (
  options: FetchOptions,
  headerName: string,
  headerValue: string,
) => {
  if (!options.headers) {
    options.headers = {
      [headerName]: headerValue,
    };
    return;
  }
  if (options.headers instanceof Headers) {
    options.headers.set(headerName, headerValue);
  } else if (Array.isArray(options.headers)) {
    options.headers.push([headerName, headerValue]);
  } else {
    options.headers[headerName] = headerValue;
  }
};

/**
 * Preview mode ("view as role") is read-only server-side: any non-GET request
 * carrying the preview header is rejected with 409 PREVIEW_MODE_READ_ONLY.
 * Routine tracking writes (activity progress, content metrics) fire during
 * normal browsing, so that rejection is expected and swallowed centrally —
 * the write becomes a silent no-op instead of an error toast.
 */
const isPreviewReadOnlyError = (err: unknown): boolean =>
  err instanceof FetchError &&
  err.response?.status === 409 &&
  (err.data as { error?: string } | undefined)?.error ===
    'PREVIEW_MODE_READ_ONLY';

export const useApi = (apiOptions?: { skipAuth?: true }) => {
  const config = useRuntimeConfig();
  const headers = useRequestHeaders(['cookie']);
  // Captured on the server: the ID token the auth service worker added to the
  // incoming navigation request (empty object / undefined in the browser).
  const incomingAuthorization = useRequestHeaders([
    'authorization',
  ]).authorization;

  const { organization } = useOrganizationStore();
  const previewStore = usePreviewStore();

  const client = $fetch.create({
    retry: 0,
    baseURL: resolveApiBaseUrl(config, import.meta.server),
    credentials: 'include',
    headers,
    async onRequest({ options }) {
      setDefaultHeader(options, 'request-id', crypto.randomUUID());
      if (import.meta.client) {
        setDefaultHeader(
          options,
          'time-zone',
          Intl.DateTimeFormat().resolvedOptions().timeZone,
        );
      }

      if (organization?.slug !== undefined) {
        setDefaultHeader(
          options,
          organizationSlugHeaderName,
          organization?.slug,
        );
      }
      if (organization?.customDomain) {
        setDefaultHeader(
          options,
          organizationDomainHeaderName,
          organization?.customDomain,
        );
      }

      if (apiOptions?.skipAuth !== true) {
        const authToken = await resolveAuthToken(incomingAuthorization);
        if (authToken) {
          setDefaultHeader(options, 'Authorization', `Bearer ${authToken}`);
        }
      }

      if (previewStore.previewRoleId !== null) {
        setDefaultHeader(
          options,
          previewRoleHeaderName,
          String(previewStore.previewRoleId),
        );
      }
    },
  });

  const withPreviewGuard = (async (request: unknown, options?: unknown) => {
    try {
      return await client(request as never, options as never);
    } catch (err) {
      if (isPreviewReadOnlyError(err)) {
        return;
      }
      throw err;
    }
  }) as typeof client;

  return Object.assign(withPreviewGuard, client);
};

export const logError = (err: unknown) => {
  if (err instanceof FetchError) {
    consola.error({
      type: 'FetchError',
      message: err.message,
      data: err.data,
      status: err.response?.status,
    });
  } else {
    consola.error(err);
  }
  if (err instanceof Error) {
    consola.info(err.stack);
  }
};

const showUnknownError = (err: any): void => {
  showError(new Error('Unknown error', { cause: err }));
};

export const getErrorStatus = (err: unknown): number | null => {
  if (err instanceof H3Error) {
    return err.statusCode;
  }
  if (err instanceof FetchError && err.response) {
    return err.response.status;
  }

  if (typeof err !== 'object' || err === null) {
    return null;
  }

  const candidate = err as {
    statusCode?: unknown;
    status?: unknown;
    response?: { status?: unknown };
  };

  const status =
    candidate.statusCode ?? candidate.response?.status ?? candidate.status;

  return typeof status === 'number' ? status : null;
};

export const handleApiError = async (err: unknown) => {
  logError(err);

  const errorStatus = getErrorStatus(err);

  if (errorStatus === 401 || errorStatus === 403) {
    const route = useRoute();
    const authUser = useAuthUserStore();

    authUser.clearSession();

    const redirect =
      route.path === '/auth/login'
        ? undefined
        : `/auth/login?redirect=${encodeURIComponent(route.fullPath)}`;

    return await navigateTo(redirect);
  }

  throw createError({
    statusCode: errorStatus ?? 500,
    fatal: true,
    cause: err,
  });
};

export const handleError = async (err: unknown): Promise<void> => {
  logError(err);
  const errorStatus = getErrorStatus(err);

  if (errorStatus === ORGANIZATION_UNAVAILABLE_STATUS) {
    showError({ statusCode: ORGANIZATION_UNAVAILABLE_STATUS });
    return;
  }

  const authUser = useAuthUserStore();

  if (
    errorStatus === 401 ||
    errorStatus === 403 ||
    (errorStatus === 404 && !authUser.isLoggedIn)
  ) {
    const route = useRoute();

    authUser.clearSession();

    const redirect =
      route.path === '/auth/login'
        ? undefined
        : `/auth/login?redirect=${encodeURIComponent(route.fullPath)}`;

    await clearError({ redirect });
    return;
  }

  if (errorStatus === 404) {
    showError({ statusCode: 404 });
    return;
  }

  return showUnknownError(err);
};
